import {
    type CSSProperties,
    memo,
    type ReactNode,
    useCallback,
    useRef,
    useState,
} from 'react';

import { DownOutlined, MoreOutlined, UpOutlined } from '@ant-design/icons';
import { Card as AntCard, Dropdown } from 'antd';
import type { CardProps as AntCardProps } from 'antd/es/card';
import cn from 'classnames';

import { HelpPopover } from '@/shared/cms/question-mark';
import { useTheme } from '@/shared/lib/hooks';
import { Button } from '@/shared/ui/button';

import cls from './card.module.css';

export type CardProps = AntCardProps & {
    accentColor?: string | undefined;
    anchor?: string;
    className?: string;
    footerActions?: ReactNode;
    hasSnowflake?: boolean;
    hasShadow?: boolean;
    headerActions?: ReactNode;
    headerActionsDataTour?: string | undefined;
    helpId?: string | undefined;
    innerClassName?: string;
    isChild?: boolean;
    isCollapsible?: boolean;
    isFollowingCursor?: boolean;
    isFullHeight?: boolean;
    isHeadWithoutPadding?: boolean;
    isMobileFlat?: boolean;
    isWithoutPadding?: boolean;
    subtitle?: string | ReactNode;
    wrapperClassName?: string;
};

export const Card = memo((props: CardProps) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const isDarkTheme = useTheme()[2];
    const {
        accentColor,
        anchor,
        className,
        footerActions,
        hasSnowflake = true,
        hasShadow = true,
        headerActions,
        headerActionsDataTour,
        helpId,
        innerClassName,
        isChild = false,
        isCollapsible = false,
        isFollowingCursor = true,
        isFullHeight = false,
        isHeadWithoutPadding = false,
        isMobileFlat = false,
        isWithoutPadding = false,
        subtitle,
        title,
        wrapperClassName,
        ...otherProps
    } = props;

    const handleMouseMove = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (!cardRef.current || !isFollowingCursor) return;

            const rect = cardRef.current.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 100;
            const y = ((e.clientY - rect.top) / rect.height) * 100;

            cardRef.current.style.setProperty('--mouse-x', `${x}%`);
            cardRef.current.style.setProperty('--mouse-y', `${y}%`);
        },
        [isFollowingCursor],
    );

    const toggleCollapse = useCallback(() => {
        setIsCollapsed((prev) => !prev);
    }, []);

    const renderDropdownContent = useCallback(
        () => <div className={cls.headerActionsDropdown}>{headerActions}</div>,
        [headerActions],
    );

    const renderCollapseButton = () => {
        if (!isCollapsible) return null;

        return (
            <Button
                icon={isCollapsed ? <DownOutlined /> : <UpOutlined />}
                onClick={toggleCollapse}
                variant="secondary"
            />
        );
    };

    const renderHeader = () => {
        // Hide header if no title and no headerActions and no isCollapsible props
        if (!title && !headerActions && !isCollapsible) return null;

        const hasHeaderActions =
            headerActions !== undefined && headerActions !== null;

        return (
            <div
                className={cls.headerActions}
                {...(headerActionsDataTour
                    ? { 'data-tour': headerActionsDataTour }
                    : {})}
            >
                {hasHeaderActions && !isCollapsed && (
                    <>
                        <div className={cls.headerActionsDesktop}>
                            {headerActions}
                        </div>
                        <div className={cls.headerActionsMobile}>
                            <Dropdown
                                dropdownRender={renderDropdownContent}
                                trigger={['click']}
                            >
                                <Button
                                    icon={<MoreOutlined />}
                                    variant="ghost"
                                />
                            </Dropdown>
                        </div>
                    </>
                )}
                {renderCollapseButton()}
            </div>
        );
    };

    const renderTitle = () => {
        if (!title) return null;

        return (
            <div className={cls.titleWrapper}>
                <div className={cls.titleRow}>
                    <div className={cls.title}>{title}</div>
                    {helpId && <HelpPopover helpId={helpId} />}
                </div>
                {subtitle && <div className={cls.subtitle}>{subtitle}</div>}
            </div>
        );
    };

    return (
        <div
            className={cn({
                [cls.fullHeight]: isFullHeight,
            })}
        >
            <div
                ref={cardRef}
                className={cn(cls.cardWrapper, wrapperClassName, {
                    [cls.followCursor]: isFollowingCursor,
                    [cls.isMobileFlat]: isMobileFlat,
                    [cls.fullHeight]: isFullHeight,
                })}
                id={anchor}
                onMouseMove={handleMouseMove}
                style={{ '--accent-color': accentColor } as CSSProperties}
            >
                <AntCard
                    actions={footerActions ? [footerActions] : []}
                    className={cn(cls.card, className, {
                        [cls.collapsed]: isCollapsed,
                        [cls.hasShadow]: hasShadow,
                        [cls.withoutPadding]: isWithoutPadding,
                        [cls.headWithoutPadding]: isHeadWithoutPadding,
                        [cls.isChild]: isChild,
                        [cls.isMobileFlat]: isMobileFlat,
                        [cls.fullHeight]: isFullHeight,
                    })}
                    {...(isCollapsible || headerActions
                        ? { extra: renderHeader() }
                        : {})}
                    title={renderTitle()}
                    {...otherProps}
                >
                    {isDarkTheme && hasSnowflake && (
                        <div
                            className={cn(cls.snowflake, {
                                [cls.isMobileFlat]: isMobileFlat,
                            })}
                        />
                    )}
                    <div className={cn(cls.inner, innerClassName)}>
                        {otherProps.children}
                    </div>
                </AntCard>
            </div>
        </div>
    );
});

Card.displayName = 'Card';
